import { default as Select } from 'antd/es/select';
import { default as Skeleton } from 'antd/es/skeleton';
import { default as Spin } from 'antd/es/spin';
import { type OpenAPIV3_1 } from 'openapi-types';
import { useEffect, useMemo, useState } from 'react';
import { type FieldDefinition } from '../../models/endpoints';
import { fetchProduct } from '../../network/fetchProducts';
import { extractNestedFields } from '../../utils/extractNestedFields';
import { prettifyJson } from '../../utils/prettifyJson';
import { PrismCode } from '../PrismCode';
import { Example } from './Example';
import { OpenAPISchema } from './OpenAPISchema';
import { Schema } from './Schema';
import { ExampleProduct, type CurrentSchema, type Product } from './types';
import { useEndpointData } from './useEndpointData';
import { updateResponseExampleBasedOnRequestExample } from './utils/updateResponseExampleBasedOnRequestExample';
import { enhanceData } from '../../utils/enhancePayoutExample';

import { PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES } from '../../data/createPayoutData/payoutSchemasAndExamples';
import { AuthHeaders } from '../AuthHeaders';

/**
 * @disclaimer: This is similar to the OpenAPISchema component, however it's only used for the CreatePayoutPage component
 * and should not be used anywhere else for the moment
 */
export const CustomPayoutSchema = ({
  organisationId,
  schemaId,
  setSchemaId,
  selectedProductId
}: {
  organisationId: string | undefined;
  schemaId?: string;
  setSchemaId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedProductId: number;
}) => {
  const { endpointDefinitionData, loading } = useEndpointData(
    'payouts-get-details',
    null
  );

  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [schemas, setSchemas] = useState<Array<CurrentSchema> | undefined>(
    undefined
  );

  const [loadingProduct, setLoadingProduct] = useState(false);

  useEffect(() => {
    const getProduct = async () => {
      if (organisationId && selectedProductId) {
        setLoadingProduct(true);
        const result = await fetchProduct({
          orgId: organisationId,
          productId: String(selectedProductId),
          schemaId: schemaId ?? undefined
        });
        setProduct(result?.data);
        setSchemas(result?.data?.schema?.available_schemas);
        setLoadingProduct(false);
      }
    };
    getProduct();
  }, [organisationId, schemaId, selectedProductId]);

  const options = useMemo(() => {
    return (
      schemas?.map((schema) => ({
        key: schema.id,
        label: schema.label,
        value: schema.id
      })) ?? []
    );
  }, [schemas]);

  const example = useMemo(() => {
    // if we have a product and an example, use it (from network)
    if (product?.example) {
      return prettifyJson(
        enhanceData({
          countryIso: product.schema?.payout_product?.country ?? '',
          currencyIso: product.schema?.payout_product?.currency ?? '',
          example: JSON.parse(product?.example)
        })
      );
    }

    if (!selectedProductId) {
      return PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES?.[0].data.example;
    }

    // look for an existing sample
    const matchingSample = PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES?.find(
      (product: ExampleProduct) =>
        product.data.schema?.payout_product?.id === selectedProductId
    );

    // if nothing is found, fall back to the first example that you can find
    if (!matchingSample) {
      return prettifyJson(
        enhanceData({
          countryIso:
            PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES?.[0]?.data.schema.payout_product
              .country,
          currencyIso:
            PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES?.[0]?.data.schema.payout_product
              .currency,
          example: JSON.parse(
            PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES?.[0]?.data.example
          )
        })
      );
    }

    return prettifyJson(
      enhanceData({
        countryIso: matchingSample?.data.schema.payout_product.country,
        currencyIso: matchingSample?.data.schema.payout_product.currency,
        example: JSON.parse(matchingSample?.data.example ?? '')
      })
    );
  }, [
    product?.example,
    product?.schema?.payout_product?.country,
    product?.schema?.payout_product?.currency,
    selectedProductId
  ]);

  const schema = useMemo(() => {
    // a product was found
    if (product) {
      return extractNestedFields(product.schema?.schema_fields) ?? [];
    }

    const matchingSample = PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES.find(
      (product: ExampleProduct) =>
        product?.data?.schema?.payout_product?.id === selectedProductId
    );

    // a matching sample was found
    if (matchingSample) {
      return (
        extractNestedFields(matchingSample?.data.schema.schema_fields) ?? []
      );
    }
    // nothing was found
    return extractNestedFields(
      PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES?.[0]?.data.schema.schema_fields
    );
  }, [product, selectedProductId]);

  if (!endpointDefinitionData?.responses?.['200']) {
    return <Skeleton active />;
  }

  const responseExample = updateResponseExampleBasedOnRequestExample(
    endpointDefinitionData?.responses?.['200'],
    example
  );

  if (loading) return <Skeleton active />;

  if (!endpointDefinitionData) return null;

  return (
    <div className="flex flex-row flex-wrap justify-between gap-8 lg:flex-nowrap">
      <div className="flex flex-col">
        <div className="flex flex-col gap-4">
          <p className="text-2xl">Request headers</p>
          <div className="w-2/3 border-b border-gray-300"></div>
        </div>
        <AuthHeaders />
        <div
          className="flex w-full flex-col gap-8 lg:w-1/2"
          data-testid="request-attributes"
        >
          <div className="flex flex-col gap-4">
            <div className="text-2xl">Request Body</div>
            <div className="w-2/3 border-b border-gray-300"></div>
          </div>

          <div className="flex flex-col gap-6">
            {schema?.map((field: FieldDefinition) => {
              return (
                <OpenAPISchema
                  field={{
                    name: field.field,
                    fields: field.fields,
                    type: field.type,
                    rules: field.rules,
                    description: field.description,
                    items: field.fields,
                    customRequired: false
                  }}
                  key={`${field.field}-param`}
                />
              );
            })}
          </div>

          <div className="flex flex-col gap-6">
            <div className="w-2/3 border-b border-gray-300"></div>
            {endpointDefinitionData.responses?.['200'] ? (
              <Schema
                input={
                  endpointDefinitionData.responses?.[
                    '200'
                  ] as OpenAPIV3_1.ResponseObject
                }
                title="Response Schema"
              />
            ) : null}
          </div>
        </div>
      </div>

      <div
        className="flex w-full flex-col gap-8 lg:w-1/2"
        data-testid="request-examples"
      >
        {options.length > 1 ? (
          loadingProduct ? (
            <Spin />
          ) : (
            <Select
              placeholder="Select a schema"
              defaultValue={options?.[0]}
              value={options?.find(
                (option) => option.key?.toString() === schemaId
              )}
              onChange={(newItem) => {
                setSchemaId(newItem.toString());
              }}
              options={options}
              size="large"
            />
          )
        ) : null}

        <PrismCode
          key={product?.schema?.payout_product?.id}
          title="Request"
          code={example}
          language="js"
        />

        <div className="flex flex-col gap-6">
          {!responseExample ? (
            <Spin />
          ) : (
            <Example
              title="Response"
              input={responseExample as OpenAPIV3_1.ResponseObject}
            />
          )}
        </div>
      </div>
    </div>
  );
};
